import * as React from "react";
import { Phone, PhoneCall, Loader2, CheckCircle2, XCircle, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface VoiceNumber {
  integrationId: string;
  name: string;
  phoneNumber: string | null;
}

type Outcome =
  | "in-progress"
  | "connected"
  | "busy"
  | "no-answer"
  | "failed"
  | null;

interface CallStatus {
  callId: string;
  status: string;
  phase: "queued" | "ringing" | "in-progress" | "ended";
  ended: boolean;
  endedReason: string | null;
  outcome: Outcome;
  durationSeconds: number | null;
  transcript: string | null;
}

function fmtDuration(sec: number | null): string {
  if (sec == null) return "—";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function badgeVariant(o: Outcome): "default" | "secondary" | "destructive" | "outline" {
  switch (o) {
    case "connected":
      return "default";
    case "failed":
      return "destructive";
    case "busy":
    case "no-answer":
      return "secondary";
    default:
      return "outline";
  }
}

/**
 * Central "call myself" test for a centrally-managed voice employee. Proxies to
 * the instance via the provisioning API. admin-ui has no Dialog primitive, so
 * this is a lightweight fixed overlay.
 */
export function VoiceTestCallModal({
  instanceName,
  employeeId,
  open,
  onClose,
}: {
  instanceName: string;
  employeeId: string;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation("instances");
  const base = `/api/admin/instances/${instanceName}/employees/${employeeId}/voice-test`;
  const [numbers, setNumbers] = React.useState<VoiceNumber[]>([]);
  const [loadingNumbers, setLoadingNumbers] = React.useState(false);
  const [fromId, setFromId] = React.useState("");
  const [toNumber, setToNumber] = React.useState("");
  const [placing, setPlacing] = React.useState(false);
  const [callId, setCallId] = React.useState<string | null>(null);
  const [status, setStatus] = React.useState<CallStatus | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const pollRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = React.useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  React.useEffect(() => {
    if (!open) return;
    setError(null);
    setCallId(null);
    setStatus(null);
    setPlacing(false);
    setLoadingNumbers(true);
    api
      .get<VoiceNumber[]>(`${base}/numbers`)
      .then((list) => {
        const arr = Array.isArray(list) ? list : [];
        setNumbers(arr);
        setFromId(arr.length === 1 ? arr[0].integrationId : "");
      })
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : String(e))
      )
      .finally(() => setLoadingNumbers(false));
    return () => stopPolling();
  }, [open, base, stopPolling]);

  React.useEffect(() => {
    if (!callId) return;
    stopPolling();
    const tick = async () => {
      try {
        const s = await api.get<CallStatus>(
          `${base}/status/${encodeURIComponent(callId)}`
        );
        setStatus(s);
        if (s.ended) stopPolling();
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : String(e));
        stopPolling();
      }
    };
    void tick();
    pollRef.current = setInterval(tick, 2000);
    return () => stopPolling();
  }, [callId, base, stopPolling]);

  if (!open) return null;

  const canCall =
    !!toNumber.trim() &&
    numbers.length > 0 &&
    (numbers.length <= 1 || !!fromId) &&
    !placing;

  const placeCall = async () => {
    if (!canCall) return;
    setPlacing(true);
    setError(null);
    setStatus(null);
    setCallId(null);
    try {
      const res = await api.post<{ callId: string }>(`${base}/call`, {
        toNumber: toNumber.trim(),
        fromIntegrationId: fromId || undefined,
      });
      setCallId(res.callId);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPlacing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-[480px] rounded-lg border bg-background p-5 shadow-lg">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold">{t("voiceTest.title")}</h2>
            <p className="text-sm text-muted-foreground">
              {t("voiceTest.description")}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
            aria-label={t("voiceTest.close")}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4">
          {numbers.length > 1 && (
            <div className="space-y-1.5">
              <Label htmlFor="vtc-from">{t("voiceTest.callFrom")}</Label>
              <Select value={fromId} onValueChange={setFromId}>
                <SelectTrigger id="vtc-from">
                  <SelectValue placeholder={t("voiceTest.selectNumber")} />
                </SelectTrigger>
                <SelectContent>
                  {numbers.map((n) => (
                    <SelectItem key={n.integrationId} value={n.integrationId}>
                      {n.name}
                      {n.phoneNumber ? ` · ${n.phoneNumber}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="vtc-to">{t("voiceTest.callTo")}</Label>
            <Input
              id="vtc-to"
              value={toNumber}
              onChange={(e) => setToNumber(e.target.value)}
              placeholder="+491701234567"
              inputMode="tel"
              disabled={placing}
            />
            <p className="text-xs text-muted-foreground">
              {t("voiceTest.creditsHint")}
            </p>
          </div>

          {!loadingNumbers && numbers.length === 0 && (
            <p className="text-sm text-destructive">
              {t("voiceTest.noNumber")}
            </p>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}

          {status && (
            <div className="space-y-2 rounded-md border p-3">
              <div className="flex items-center gap-2">
                {status.ended ? (
                  status.outcome === "connected" ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                  ) : (
                    <XCircle className="h-4 w-4 text-muted-foreground" />
                  )
                ) : (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                <span className="text-sm font-medium capitalize">
                  {status.ended ? status.outcome ?? "ended" : status.phase}
                </span>
                <Badge variant={badgeVariant(status.outcome)} className="ml-auto">
                  {status.ended ? fmtDuration(status.durationSeconds) : status.phase}
                </Badge>
              </div>

              {status.ended && status.endedReason && (
                <p className="break-all text-xs text-muted-foreground">
                  {status.endedReason}
                </p>
              )}

              {status.transcript && (
                <div className="max-h-40 overflow-y-auto whitespace-pre-wrap rounded bg-muted p-2 text-xs">
                  {status.transcript}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={placing}>
            {t("voiceTest.close")}
          </Button>
          <Button onClick={placeCall} disabled={!canCall}>
            {placing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("voiceTest.calling")}
              </>
            ) : (
              <>
                {callId ? (
                  <PhoneCall className="mr-2 h-4 w-4" />
                ) : (
                  <Phone className="mr-2 h-4 w-4" />
                )}
                {callId ? t("voiceTest.callAgain") : t("voiceTest.call")}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
